import { Module } from '@/shared/types/permissions';

/**
 * Maps the permission groups the designs speak in ("AI agents", "Chat") onto the flat
 * permission rows the API stores (`brains.write`). Nothing in the API describes this
 * grouping, so it lives here as plain data.
 */

export type PermissionLevel = 'no_access' | 'read' | 'edit' | 'reply';

export type PermissionVerb = 'read' | 'write' | 'delete' | 'assign';

const ALL_VERBS: PermissionVerb[] = ['read', 'write', 'delete'];

const FAMILY_VERBS: Partial<Record<Module, PermissionVerb[]>> = {
  analytics: ['read'],
  audit_logs: ['read'],
  billing: ['read', 'write'],
  invitations: ['read', 'write'],
  logs: ['read'],
  roles: ['read', 'write', 'delete', 'assign'],
  speech_strategy: ['read', 'write'],
};

export const familyVerbs = (family: Module): PermissionVerb[] => FAMILY_VERBS[family] ?? ALL_VERBS;

export type PermissionToggle = {
  key: string;
  labelKey: string;
  descriptionKey: string;
  ownerOnly?: boolean;
  owns: { family: Module; verbs: PermissionVerb[] };
};

export type PermissionGroup = {
  key: string;
  labelKey: string;
  families: Module[];
  ownerOnly?: boolean;
  /** Chat reads or replies, with no delete step. Defaults to the standard scale. */
  levelSet?: 'chat';
  toggles?: PermissionToggle[];
  scope?: 'environments' | 'conversations';
  scopedFamilies?: Module[];
};

// One writing level, which carries delete wherever the group has it.
const STANDARD_LEVELS: PermissionLevel[] = ['read', 'edit', 'no_access'];
const CHAT_LEVELS: PermissionLevel[] = ['read', 'reply', 'no_access'];

const LEVEL_RANK: Record<PermissionLevel, number> = { no_access: 0, read: 1, reply: 2, edit: 3 };

export const levelAtLeast = (level: PermissionLevel | undefined, floor: PermissionLevel) =>
  LEVEL_RANK[level ?? 'no_access'] >= LEVEL_RANK[floor];

export const LEVEL_VERBS: Record<PermissionLevel, PermissionVerb[]> = {
  no_access: [],
  read: ['read'],
  edit: ['read', 'write', 'delete', 'assign'],
  reply: ['read', 'write'],
};

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: 'ai_agents',
    labelKey: 'roles.groups.ai_agents',
    families: ['brains', 'speech_strategy'],
    toggles: [
      {
        key: 'view_chat_logs',
        labelKey: 'roles.toggles.view_chat_logs',
        descriptionKey: 'roles.toggles.view_chat_logs_description',
        owns: { family: 'logs', verbs: ['read'] },
      },
    ],
  },
  { key: 'knowledge', labelKey: 'roles.groups.knowledge', families: ['collections'] },
  { key: 'campaigns', labelKey: 'roles.groups.campaigns', families: ['broadcasts', 'campaigns'] },
  {
    key: 'contacts',
    labelKey: 'roles.groups.contacts',
    families: ['contacts', 'contact_field_definitions'],
  },
  {
    key: 'analytics',
    labelKey: 'roles.groups.analytics',
    families: ['analytics', 'filters', 'reports'],
    toggles: [
      {
        key: 'edit_reports',
        labelKey: 'roles.toggles.edit_reports',
        descriptionKey: 'roles.toggles.edit_reports_description',
        owns: { family: 'reports', verbs: ['write', 'delete'] },
      },
    ],
  },
  { key: 'connections', labelKey: 'roles.groups.connections', families: ['integrations'] },
  { key: 'rules', labelKey: 'roles.groups.rules', families: ['rules'] },
  {
    key: 'dev_tools',
    labelKey: 'roles.groups.dev_tools',
    families: ['credentials', 'language_models', 'speech_models', 'plugins'],
    toggles: [
      {
        key: 'edit_api_keys',
        labelKey: 'roles.toggles.edit_api_keys',
        descriptionKey: 'roles.toggles.edit_api_keys_description',
        owns: { family: 'credentials', verbs: ['write', 'delete'] },
      },
      {
        key: 'edit_llms',
        labelKey: 'roles.toggles.edit_llms',
        descriptionKey: 'roles.toggles.edit_llms_description',
        owns: { family: 'language_models', verbs: ['write', 'delete'] },
      },
      {
        key: 'edit_speech_models',
        labelKey: 'roles.toggles.edit_speech_models',
        descriptionKey: 'roles.toggles.edit_speech_models_description',
        owns: { family: 'speech_models', verbs: ['write', 'delete'] },
      },
      {
        key: 'edit_event_notifications',
        labelKey: 'roles.toggles.edit_event_notifications',
        descriptionKey: 'roles.toggles.edit_event_notifications_description',
        owns: { family: 'plugins', verbs: ['write', 'delete'] },
      },
    ],
  },
  {
    key: 'environments',
    labelKey: 'roles.groups.environments',
    families: ['desks', 'departments', 'business_hours', 'bundles'],
    scope: 'environments',
    scopedFamilies: ['desks'],
  },
  {
    key: 'chat',
    labelKey: 'roles.groups.chat',
    families: ['conversations', 'macros'],
    levelSet: 'chat',
    scope: 'conversations',
    toggles: [
      {
        key: 'edit_quick_responses',
        labelKey: 'roles.toggles.edit_quick_responses',
        descriptionKey: 'roles.toggles.edit_quick_responses_description',
        owns: { family: 'macros', verbs: ['write', 'delete'] },
      },
    ],
  },
  {
    key: 'settings',
    labelKey: 'roles.groups.settings',
    families: ['roles', 'teams'],
    toggles: [
      {
        key: 'view_activity_history',
        labelKey: 'roles.toggles.view_activity_history',
        descriptionKey: 'roles.toggles.view_activity_history_description',
        owns: { family: 'audit_logs', verbs: ['read'] },
      },
      {
        key: 'invite_members',
        labelKey: 'roles.toggles.invite_members',
        descriptionKey: 'roles.toggles.invite_members_description',
        owns: { family: 'invitations', verbs: ['read', 'write'] },
      },
      {
        key: 'manage_roles',
        labelKey: 'roles.toggles.manage_roles',
        descriptionKey: 'roles.toggles.manage_roles_description',
        ownerOnly: true,
        owns: { family: 'roles', verbs: ['write', 'delete', 'assign'] },
      },
    ],
  },
  { key: 'billing', labelKey: 'roles.groups.billing', families: ['billing'], ownerOnly: true },
];

const familyOwnedVerbs = (group: PermissionGroup, family: Module): PermissionVerb[] =>
  (group.toggles ?? []).flatMap((toggle) =>
    toggle.owns?.family === family ? toggle.owns.verbs : []
  );

export const levelVerbsForFamily = (
  group: PermissionGroup,
  family: Module,
  level: PermissionLevel
): PermissionVerb[] => {
  const owned = familyOwnedVerbs(group, family);
  if (owned.includes('read')) {
    return [];
  }
  const available = familyVerbs(family);
  return LEVEL_VERBS[level].filter((verb) => available.includes(verb) && !owned.includes(verb));
};

export const familyVerbsInGroup = (group: PermissionGroup, family: Module): PermissionVerb[] => [
  ...levelVerbsForFamily(group, family, 'edit'),
  ...familyOwnedVerbs(group, family),
];

const groupVerbs = (group: PermissionGroup): Set<PermissionVerb> =>
  new Set(group.families.flatMap((family) => familyVerbsInGroup(group, family)));

const levelsFor = (group: PermissionGroup): PermissionLevel[] => {
  if (group.levelSet === 'chat') {
    return CHAT_LEVELS;
  }
  const verbs = groupVerbs(group);

  return STANDARD_LEVELS.filter(
    (level) => level !== 'edit' || verbs.has('write') || verbs.has('delete')
  );
};

const LEVELS_BY_GROUP = new Map<string, PermissionLevel[]>(
  PERMISSION_GROUPS.map((group) => [group.key, levelsFor(group)])
);

export const groupLevels = (group: PermissionGroup): PermissionLevel[] =>
  LEVELS_BY_GROUP.get(group.key) ?? levelsFor(group);

export const levelHasExtras = (level: PermissionLevel) => level !== 'no_access' && level !== 'read';

export const maxGroupLevel = (group: PermissionGroup): PermissionLevel => {
  const levels = groupLevels(group).filter((level) => level !== 'no_access');
  return levels[levels.length - 1] ?? 'no_access';
};

export const PERMISSION_LEVEL_LABEL_KEYS: Record<PermissionLevel, string> = {
  no_access: 'roles.levels.no_access',
  read: 'roles.levels.read',
  edit: 'roles.levels.edit',
  reply: 'roles.levels.reply',
};

export const CONVERSATION_SCOPES = ['all', 'assigned', 'department'] as const;

export type ConversationScope = (typeof CONVERSATION_SCOPES)[number];

export const CONVERSATION_SCOPE_LABEL_KEYS: Record<ConversationScope, string> = {
  all: 'roles.scopes.conversations_all',
  assigned: 'roles.scopes.conversations_assigned',
  department: 'roles.scopes.conversations_department',
};

const READ_OTHERS = 'conversations.read.others';
const READ_UNASSIGNED = 'conversations.read.unassigned';
const READ_DEPARTMENTS_OTHERS = 'conversations.read.departments.others';
export const READ_BRAIN = 'conversations.read.brain';

export const CONVERSATION_SCOPES_BY_REACH: ConversationScope[] = ['all', 'department', 'assigned'];

export const CONVERSATION_GROUP_KEY = 'chat';

export const CONVERSATION_SCOPE_PERMISSIONS: Record<ConversationScope, string[]> = {
  all: [READ_OTHERS, READ_UNASSIGNED, READ_DEPARTMENTS_OTHERS],
  assigned: [],
  department: [READ_OTHERS, READ_UNASSIGNED],
};

export const ENVIRONMENT_SCOPES = ['all', 'selected'] as const;

export type EnvironmentScope = (typeof ENVIRONMENT_SCOPES)[number];

export const ENVIRONMENT_SCOPE_LABEL_KEYS: Record<EnvironmentScope, string> = {
  all: 'roles.scopes.environments_all',
  selected: 'roles.scopes.environments_selected',
};
